import { Component, computed, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  building07Icon,
  BxNavAccountCardComponent,
  BxSidenavSlimComponent,
  cpuChip01Icon,
  file06Icon,
  homeLineIcon,
  key02Icon,
  MainLogoInfo,
  monitor05Icon,
  settings01Icon,
  Sidenav,
  injectBxIcons,
} from '@beyonnex/bx-angular-ui';
import { AvvvatarsComponent } from '@ngneat/avvvatars';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';
import { EnvironmentService } from '../environments/environment.service';
import { ROUTES } from './app.models';
import { AuthService } from './services/auth/auth.service';
import { PermissionService } from './services/permission/permission.service';
import { ScreenSizeService } from './services/screen-size/screen-size.service';

@Component({
  selector: 'bxi-root',
  imports: [RouterOutlet, BxSidenavSlimComponent, AvvvatarsComponent, BxNavAccountCardComponent, TranslatePipe],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex', '[class]': 'screenSizeStyles()' },
})
export class AppComponent implements OnInit {
  private readonly environmentService = inject(EnvironmentService);
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly titleService = inject(Title);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly screenSize = inject(ScreenSizeService);
  private readonly meta = inject(Meta);

  protected readonly screenSizeStyles = toSignal(this.screenSize.getScreenSizeStyles());
  private readonly canAccessElsImport = toSignal(this.permissionService.canAccessElsImport$, { initialValue: false });
  private readonly canAccessSrtFuota = toSignal(this.permissionService.canAccessSrtFuota$, { initialValue: false });

  protected readonly sidenav = computed<Sidenav>(() => ({
    navItems: [
      {
        label: this.translateService.instant('navigation.home'),
        icon: 'home-line',
        url: ROUTES.HOME,
      },
      {
        label: this.translateService.instant('navigation.properties'),
        icon: 'building-07',
        url: ROUTES.PROPERTIES,
      },
      {
        label: this.translateService.instant('navigation.devices'),
        icon: 'monitor-05',
        url: ROUTES.DEVICES,
      },
      ...(this.canAccessElsImport()
        ? [{ label: this.translateService.instant('navigation.elsImport'), icon: 'file-06', url: ROUTES.ELS_IMPORT }]
        : []),
      ...(this.canAccessSrtFuota()
        ? [
            {
              label: this.translateService.instant('navigation.srtFuota'),
              icon: 'cpu-chip-01',
              url: this.environmentService.fuotaCampaignManagerUrl,
            },
          ]
        : []),
    ],
    footerButtons: [{ url: ROUTES.SETTINGS, icon: 'settings-01', tooltip: this.translateService.instant('navigation.settings') }],
  }));

  protected readonly logo = computed<MainLogoInfo>(() => {
    switch (this.environmentService.environment) {
      case 'dev':
        return { logoUrl: 'assets/images/bxInsight_symbol_dev.svg', alt: 'bxInsight' };
      case 'qa':
        return { logoUrl: 'assets/images/bxInsight_symbol_qa.svg', alt: 'bxInsight' };
      case 'prod':
        return { logoUrl: 'assets/images/bxInsight_symbol.svg', alt: 'bxInsight' };
    }
  });

  protected readonly isAuthenticated = toSignal(this.authService.isAuthenticated$);
  protected readonly userData = toSignal(this.authService.userData$);

  constructor() {
    injectBxIcons([settings01Icon, homeLineIcon, building07Icon, monitor05Icon, file06Icon, cpuChip01Icon, key02Icon]);
    this.handleTabTitle();
  }

  ngOnInit(): void {
    this.authService.handleAuth();
    this.setMetaTags();
  }

  private handleTabTitle(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let r = this.route;
          while (r.firstChild) r = r.firstChild;
          return r;
        }),
        map((r) => r.snapshot.data),
        takeUntilDestroyed(),
      )
      .subscribe((data) => {
        const title = data['title'];
        if (title) {
          this.titleService.setTitle(title);
        }
      });
  }

  protected logout(): void {
    this.authService.logout();
  }

  private setMetaTags(): void {
    this.meta.updateTag({ name: 'og:image', content: `https://${this.environmentService.domain}/assets/images/bxInsight.png` });
    this.meta.updateTag({ property: 'og:image:width', content: '285' });
    this.meta.updateTag({ property: 'og:image:height', content: '75' });
    this.meta.updateTag({ property: 'og:image:alt', content: 'UI to manage all DDM services.' });
  }
}
